import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { managedArchivePath } from './components/QmsArchiveAsset';

const API = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
const routes = {
  'hydrate-change': id => [`/quality/engineering-changes/${encodeURIComponent(id)}/hydrate`, 'POST'],
  'update-pe': id => [`/quality/pe-actions/${encodeURIComponent(id)}`, 'PATCH'],
  'consolidate-change': () => ['/quality/change-control-consolidation', 'POST'],
};
const sha256 = async file => Array.from(new Uint8Array(await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer())))
  .map(value => value.toString(16).padStart(2, '0')).join('');
function download(name, value) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }));
  const link = document.createElement('a'); link.href = url; link.download = name; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function resolveArchiveLinks(value, archives) {
  if (typeof value === 'string' && /^archive:[a-f0-9]{64}$/.test(value)) {
    const archive = archives[value.slice(8)];
    if (!archive?.archiveUrl) throw new Error('A required source asset has not been archived.');
    validateArchiveReceipts({ [value.slice(8)]: archive });
    return archive.archiveUrl;
  }
  if (Array.isArray(value)) return value.map(item => resolveArchiveLinks(item, archives));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, resolveArchiveLinks(item, archives)]));
  return value;
}
export function validateArchiveReceipts(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length > 5000) throw new Error('Choose valid QMS archive receipts.');
  for (const [hash, receipt] of Object.entries(value)) {
    if (!/^[a-f0-9]{64}$/.test(hash) || !receipt || typeof receipt !== 'object' || Array.isArray(receipt)
      || receipt.sha256 !== hash || !/^[a-f0-9]{24}$/.test(receipt.archiveId || '')
      || managedArchivePath(receipt.archiveUrl) !== `/quality/archives/${receipt.archiveId}/download`
      || receipt.downloadPath !== `/quality/archives/${receipt.archiveId}/download`
      || !Number.isSafeInteger(receipt.size) || receipt.size < 1 || receipt.size > 50 * 1024 * 1024
      || typeof receipt.name !== 'string' || !receipt.name || receipt.name.length > 500) throw new Error('An archive receipt has a mismatched checksum, identity, address or size.');
  }
  return value;
}
export function validateMigrationManifest(value) {
  if (!value || !Array.isArray(value.operations) || value.operations.length > 1000
    || value.operations.some(op => !routes[op.type] || !op.payload || typeof op.payload !== 'object'
      || (op.type !== 'consolidate-change' && (typeof op.id !== 'string' || !op.id)))) throw new Error('Choose a reviewed migration manifest.');
  return value;
}
export function maintenanceError(data, status) {
  const message = (typeof data?.message === 'string' && data.message) || (typeof data?.error === 'string' && data.error) || `Request failed (${status}).`;
  const details = [];
  if (typeof data?.diagnostic === 'string' && /^[A-Z_]{1,80}$/.test(data.diagnostic) && data.diagnostic === data.diagnostic.trim()) details.push(data.diagnostic);
  if (Number.isInteger(data?.providerStatus) && data.providerStatus >= 400 && data.providerStatus <= 599) details.push(`provider HTTP ${data.providerStatus}`);
  const allowedHints = new Set(['PUBLIC_ID', 'FILENAME', 'EXTENSION', 'FORMAT', 'FILE', 'ASSET_FOLDER', 'UPLOAD_PRESET', 'OVERWRITE',
    'SIGNATURE', 'TIMESTAMP', 'CREDENTIALS', 'AUTHENTICATED', 'RAW', 'RESOURCE_TYPE', 'DELIVERY_TYPE', 'ACCOUNT', 'PLAN',
    'RESTRICTED', 'UNSUPPORTED', 'MISSING', 'INVALID', 'TOO_LONG', 'DUPLICATE', 'SIZE_LIMIT']);
  const hints = Array.isArray(data?.providerHints) ? [...new Set(data.providerHints.filter(value => allowedHints.has(value)))].slice(0, 8) : [];
  if (hints.length) details.push(`provider hints ${hints.join(', ')}`);
  return `${message}${details.length ? ` (${details.join('; ')})` : ''}`;
}

// An NI maintenance route, deliberately absent from ordinary QMS navigation.
// It uses the same authenticated, version-checked domain APIs as normal editing.
export default function QmsMigrationMaintenance() {
  const { getToken } = useAuth();
  const [manifest, setManifest] = useState(null), [files, setFiles] = useState([]);
  const [archives, setArchives] = useState({}), [results, setResults] = useState([]), [proof, setProof] = useState([]);
  const [lastExport, setLastExport] = useState(null);
  const [busy, setBusy] = useState(false), [progress, setProgress] = useState(''), [error, setError] = useState('');
  const api = async (path, method = 'GET', body) => {
    const token = await getToken();
    if (!token) throw new Error('Sign in again.');
    const multipart = body instanceof FormData;
    const response = await window.fetch(`${API}${path}`, { method,
      headers: { Authorization: `Bearer ${token}`, ...(body && !multipart ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: multipart ? body : JSON.stringify(body) } : {}) });
    const data = await response.json();
    if (!response.ok || data.ok === false) throw new Error(maintenanceError(data, response.status));
    return data;
  };
  const run = async operation => { setBusy(true); setError(''); try { await operation(); } catch (err) { setError(err.message); } finally { setBusy(false); } };
  const retainExport = (name, value) => { setLastExport({ name, value }); download(name, value); };
  const exportRecords = area => run(async () => {
    const root = area === 'pe' ? '/quality/pe-actions' : '/quality/engineering-changes';
    const records = [];
    for (const templates of area === 'pe' ? [false] : [false, true]) {
      let offset = 0, more = true;
      while (more) {
        const data = await api(`${root}?limit=100&offset=${offset}${area === 'pe' ? '' : `&templates=${templates}`}`);
        for (const item of data.items) {
          const full = await api(`${root}/${item._id}`); records.push(full.record || full.action);
          setProgress(`Exported ${records.length} ${area} records`);
        }
        offset += data.items.length; more = data.pagination.hasMore;
      }
    }
    setProgress(`Export complete: ${records.length} ${area} records.`); retainExport(`qms-${area}-native-baseline.json`, records);
  });
  const exportLegacyEvidence = () => run(async () => {
    const preview = await api('/quality/change-control-consolidation'), evidence = [];
    for (const document of preview.documents) {
      evidence.push(await api(`/quality/change-control-consolidation/${encodeURIComponent(document.id)}/evidence`));
      setProgress(`Exported ${evidence.length} of ${preview.documents.length} legacy histories`);
    }
    setProgress(`Export complete: ${evidence.length} legacy histories with original metadata and revisions.`);
    retainExport('qms-legacy-consolidation-evidence.json', evidence);
  });
  const uploadAssets = () => run(async () => {
    const required = manifest?.assets || [];
    if (!required.length) throw new Error('The manifest has no assets.');
    const next = { ...archives };
    for (const file of files) {
      const sha = await sha256(file), asset = required.find(item => item.sha256 === sha);
      if (!asset || next[sha]) continue;
      if (!['pe', 'change-control'].includes(asset.context) || !/^[a-f0-9-]{36}$/.test(asset.requestId || '')) throw new Error('Asset context and stable request ID are required.');
      setProgress(`Archiving ${Object.keys(next).length + 1} of ${required.length} assets`);
      const form = new FormData(); form.append('file', file); form.append('context', asset.context); form.append('requestId', asset.requestId);
      if (asset.recordId) form.append('recordId', asset.recordId);
      const saved = await api('/quality/archives/upload', 'POST', form);
      if (saved.sha256 !== sha || saved.size !== file.size) throw new Error('The archived bytes do not match the selected source.');
      validateArchiveReceipts({ [sha]: saved }); next[sha] = saved; setArchives({ ...next }); setProof([]);
    }
    download('qms-managed-archive-receipts.json', next);
    setProgress(`Archived ${Object.keys(next).length} of ${required.length} required assets.`);
  });
  const verifyArchives = () => run(async () => {
    const receipts = Object.entries(validateArchiveReceipts(archives)), verified = [];
    if (!receipts.length) throw new Error('Load or create archive receipts first.');
    setProof([]);
    for (const [expectedSha256, receipt] of receipts) {
      setProgress(`Verifying archived bytes ${verified.length + 1} of ${receipts.length}: ${receipt.name}`);
      const token = await getToken(); if (!token) throw new Error('Sign in again.');
      const response = await window.fetch(`${API}${receipt.downloadPath}`, { headers: { Authorization: `Bearer ${token}` }, credentials: 'omit', redirect: 'error' });
      if (!response.ok) throw new Error(`Could not read ${receipt.name} (${response.status}).`);
      const blob = await response.blob(), actualSha256 = await sha256(blob);
      if (blob.size !== receipt.size || actualSha256 !== expectedSha256) throw new Error(`Archive integrity check failed for ${receipt.name}.`);
      verified.push({ archiveId: receipt.archiveId, name: receipt.name, size: blob.size, sha256: actualSha256, verifiedAt: new Date().toISOString() });
      setProof([...verified]);
    }
    download('qms-archive-byte-verification.json', verified); setProgress(`Verified exact bytes for ${verified.length} QMS archives.`);
  });
  const apply = () => run(async () => {
    validateArchiveReceipts(archives);
    const operations = manifest.operations.map(op => ({ ...op, payload: resolveArchiveLinks(op.payload, archives) }));
    const saved = [];
    for (const [index, op] of operations.entries()) {
      setProgress(`Applying ${index + 1} of ${operations.length}: ${op.id || op.payload.documentId}`);
      const [path, method] = routes[op.type](op.id);
      const response = await api(path, method, op.payload);
      const root = op.type === 'update-pe' ? '/quality/pe-actions' : '/quality/engineering-changes';
      const recordId = op.type === 'consolidate-change' ? op.payload.recordId : op.id;
      const check = await api(`${root}/${encodeURIComponent(recordId)}`), record = check.record || check.action;
      if (!record || !Number.isInteger(record.__v ?? record.version)
        || (record.__v ?? record.version) < op.payload.expectedVersion + 1) throw new Error('Saved record readback failed.');
      saved.push({ type: op.type, id: op.id || op.payload.documentId, replayed: response.replayed === true,
        version: record?.__v ?? response.version, record, ...(op.type === 'consolidate-change' ? { receipt: response } : {}) });
      setResults([...saved]);
    }
    download('qms-native-migration-readback.json', saved); setProgress(`Verified ${saved.length} operations.`);
  });
  return <main>
    <h1>QMS migration maintenance</h1>
    <p>NI maintenance for the reviewed historical migration. Daily work belongs in Change Control and the PE workspaces.</p>
    <div className="ni-toolbar" style={{ gap: 8, flexWrap: 'wrap' }}>
      <button className="ni-button" disabled={busy} onClick={() => exportRecords('change')}>Export change records</button>
      <button className="ni-button" disabled={busy} onClick={() => exportRecords('pe')}>Export PE records</button>
      <button className="ni-button" disabled={busy} onClick={() => run(async () => retainExport('qms-consolidation-preview.json', await api('/quality/change-control-consolidation')))}>Preview legacy consolidation</button>
      <button className="ni-button" disabled={busy} onClick={exportLegacyEvidence}>Export legacy consolidation evidence</button>
      <button className="ni-button" disabled={busy || !lastExport} onClick={() => download(lastExport.name, lastExport.value)}>Download export</button>
    </div>
    {lastExport && <p>Latest completed export: {lastExport.name}. Download it again without fetching the records. Available until this page is reloaded.</p>}
    <section className="ni-panel" style={{ marginTop: 20 }}>
      <label>Reviewed manifest<input type="file" accept=".json" disabled={busy} onChange={event => {
        const file = event.target.files?.[0]; if (!file) return;
        run(async () => { setManifest(validateMigrationManifest(JSON.parse(await file.text()))); setResults([]); setProgress('Manifest loaded.'); });
      }} /></label>
      {manifest && <p>{manifest.operations.length} record operations · {(manifest.assets || []).length} source assets</p>}
      <label>Source files<input type="file" multiple disabled={busy} onChange={event => setFiles(Array.from(event.target.files || []))} /></label>
      <p>{files.length} source files selected · {Object.keys(archives).length} verified archives · {results.length} completed operations</p>
      <label>Existing archive receipts<input type="file" accept=".json" disabled={busy} onChange={event => {
        const file = event.target.files?.[0]; if (file) run(async () => { setArchives(validateArchiveReceipts(JSON.parse(await file.text()))); setProof([]); });
      }} /></label>
      <div className="ni-toolbar" style={{ marginTop: 16, gap: 8 }}>
        <button className="ni-button" disabled={busy || !manifest || !files.length} onClick={uploadAssets}>Archive selected source assets</button>
        <button className="ni-button ni-button--primary" disabled={busy || !manifest?.operations.length} onClick={apply}>Apply reviewed content</button>
        <button className="ni-button" disabled={!results.length} onClick={() => download('qms-native-migration-readback.json', results)}>Download readback</button>
        <button className="ni-button" disabled={!Object.keys(archives).length} onClick={() => download('qms-managed-archive-receipts.json', archives)}>Download archive receipts</button>
        <button className="ni-button" disabled={busy || !Object.keys(archives).length} onClick={verifyArchives}>Verify archived bytes</button>
        <button className="ni-button" disabled={!proof.length} onClick={() => download('qms-archive-byte-verification.json', proof)}>Download byte verification</button>
      </div>
    </section>
    <p role="status">{progress}</p>{error && <p role="alert">{error}</p>}
  </main>;
}
